import React, {useState} from 'react';
import {
  Box,
  Grid2,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Stack,
  Paper,
  Collapse,
  Chip,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { DatePicker } from '@mui/x-date-pickers';
import { Search, Clear, ExpandLess, ExpandMore } from '@mui/icons-material';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import useGetSemesters from '../../Hooks/useGetSemester';
import useGetLocations from '../../Hooks/useGetLocation';

const validationSchema = Yup.object({
  semester_id: Yup.number().nullable(),
  location_id: Yup.number().nullable(),
  start_date: Yup.date().nullable(),
  end_date: Yup.date().nullable()
});

const AdminFilterBar = ({ onSubmit, initialFilters = {} }) => {
  const role = useSelector((state) => state.role);
  const { semesters, loading: semestersLoading } = useGetSemesters(role);
  const { locations, loading: locationsLoading } = useGetLocations(role);

  // ── New state: controls visibility ──
  const [filtersVisible, setFiltersVisible] = useState(true);

  // We'll also track if any search has been performed
  const [hasSearched, setHasSearched] = useState(false);

  const initialValues = {
    semester_id: initialFilters.semester_id || null,
    location_id: initialFilters.location_id || null,
    start_date: initialFilters.start_date || null,
    end_date: initialFilters.end_date || null,
  };

  const getActiveFilterCount = (values) => {
    let count = 0;
    if (values.semester_id) count++;
    if (values.location_id) count++;
    if (values.start_date) count++;
    if (values.end_date) count++;
    return count;
  };

  const handleSubmitInternal = (values, { resetForm }) => {
    let start_date_parse = values.start_date 
      ? DateTime.fromJSDate(new Date(values.start_date)).toSQLDate() 
      : null;
    let end_date_parse = values.end_date 
      ? DateTime.fromJSDate(new Date(values.end_date)).toSQLDate() 
      : null;

    onSubmit({
      ...values,
      start_date: start_date_parse,
      end_date: end_date_parse,
    });

    // After successful submit → hide filters & mark as searched
    setHasSearched(true);
    setFiltersVisible(false);
  };

  const handleClear = (resetForm) => {
    resetForm();
    onSubmit({
      semester_id: null,
      location_id: null,
      start_date: null,
      end_date: null,
    });
    // Optionally keep filters visible after clear, or hide — your choice
    // setFiltersVisible(false);  
  };

  const toggleFilters = () => setFiltersVisible(prev => !prev);

  return (
    <Box sx={{ mb: 3 }}>
      {/* Always-visible header / toggle area */}
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center" 
        sx={{ mb: 1.5, mt: 1 }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Filters
          {getActiveFilterCount(initialFilters) > 0 && (
            <Chip 
              label={`${getActiveFilterCount(initialFilters)} active`} 
              size="small" 
              color="primary" 
              sx={{ ml: 1.5 }}
            />
          )}
        </Typography>

        <Button
          size="small"
          sx={{maxWidth: 200}}
          onClick={toggleFilters}
          startIcon={filtersVisible ? <ExpandLess /> : <ExpandMore />}
          variant={hasSearched ? "outlined" : "text"}
          color={hasSearched ? "primary" : "inherit"}
        >
          {filtersVisible 
            ? "Hide filters" 
            : hasSearched 
              ? "Edit filters" 
              : "Show filters"}
        </Button>
      </Stack>

      {/* Collapsible content */}
      <Collapse in={filtersVisible}>
        <Paper sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmitInternal}
            enableReinitialize
          >
            {({ values, handleChange, handleBlur, setFieldValue, resetForm }) => {
              const activeCount = getActiveFilterCount(values);

              return (
                <Form>
                  <Grid2 container spacing={2} alignItems="flex-end">
                    {/* Location */}
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Location</InputLabel>
                        <Field
                          as={Select}
                          name="location_id"
                          label="Location"
                          value={values.location_id || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={locationsLoading}
                        >
                          <MenuItem value=""><em>All Locations</em></MenuItem>
                          {locations?.map(loc => (
                            <MenuItem key={loc.id} value={loc.id}>
                              {loc.name}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </Grid2>

                    {/* Semester */}
                    <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Semester</InputLabel>
                        <Field
                          as={Select}
                          name="semester_id"
                          label="Semester"
                          value={values.semester_id || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={semestersLoading}
                        >
                          <MenuItem value=""><em>All Semesters</em></MenuItem>
                          {semesters?.map(sem => (
                            <MenuItem key={sem.id} value={sem.id}>
                              {sem.year} - {sem.title}
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </Grid2>

                    {/* Dates */}
                    <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                      <DatePicker
                        label="Start Date"
                        value={values.start_date}
                        onChange={val => setFieldValue('start_date', val)}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6, md: 2 }}>
                      <DatePicker
                        label="End Date"
                        value={values.end_date}
                        onChange={val => setFieldValue('end_date', val)}
                        slotProps={{ textField: { size: 'small', fullWidth: true } }}
                      />
                    </Grid2>

                    {/* Buttons */}
                    <Grid2 size={{ xs: 12, md: 2 }}>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="outlined"
                          color="inherit"
                          size="small"
                          startIcon={<Clear />}
                          onClick={() => handleClear(resetForm)}
                          disabled={activeCount === 0}
                          fullWidth
                        >
                          Clear
                        </Button>
                        <Button
                          variant="contained"
                          color='secondary'
                          startIcon={<Search />}
                          type="submit"
                          size="small"
                          fullWidth
                        >
                          Search
                        </Button>
                      </Stack>
                    </Grid2>
                  </Grid2>
                </Form>
              );
            }}
          </Formik>
        </Paper>
      </Collapse>
    </Box>
  );
};

export default AdminFilterBar;
